import React, { Fragment, RefObject } from "react";
import { RouteComponentProps } from "react-router";
import {
  request,
  handleValidationErrors,
  responseHandler,
} from "../helpers/utilities";
import { Spin, message } from "antd";
import Card from "../components/Card";
import { Role, AUMTarget, FormFieldConfig } from "../helpers/shared-interfaces";
import BaseForm from "../components/Form/BaseForm";
import { AxiosResponse } from "axios";
import { FormInstance } from "antd/lib/form";
import * as Sentry from "@sentry/browser";
import { GET_AUM_TARGET_SUMMARY, UPDATE_AUM_TARGET } from "../gql/aum";
import { CURRENT_PERIOD_QUERY } from "../gql/general";
import { ALL_ROLES_QUERY } from "../gql/roles";
import { generateDisplayErrorMessage } from "../helpers/utilities";


interface State {
  id: string | null;
  isLoading: boolean;
  roles: Role[];
  aumTarget: AUMTarget | null;
  currentPeriod: string[];
}

interface RouteParams {
  id: string;
}

interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
}

enum duplicateError {
  property = "roleId",
  description = "has already been taken",
}

export default class UpdateAUMTarget extends React.Component<Props, State> {
  reference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      id: null,
      isLoading: true,
      roles: [],
      aumTarget: null,
      currentPeriod: [],
    };
    this.reference = React.createRef<FormInstance>();

    this.redirectToAllAUMTargetsPage =
      this.redirectToAllAUMTargetsPage.bind(this);
    this.updateAUMTarget = this.updateAUMTarget.bind(this);
  }

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  async updateAUMTarget(): Promise<void> {
    const { id } = this.state;
    const fieldsToBeValidated: string[] = ["roleId", "amount"];

    interface Validated {
      roleId: string;
      amount: number;
    }

    interface Response {
      data: {
        data: {
          updateAumTarget: {
            aumTarget: AUMTarget;
            errors: [{ property: string; description: string }];
          };
        };
      };
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_AUM_TARGET,
        variables: {
          id: id,
          roleId: data.roleId,
          amount: data.amount,
        },
      });

      this.setState({ isLoading: false });

      const { handled, err } = responseHandler(response, "updateAumTarget");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        for (const error of err) {
          if (
            error.property === duplicateError.property &&
            error.description === duplicateError.description
          ) {
            message.error(`AUM Target for this role exists already - ${generateDisplayErrorMessage(err)}`);
          } else {
            message.error(`Failed to create AUM-Target - ${generateDisplayErrorMessage(err)}`);
          }
        }
      } else {
        message.success("AUM-Target update sucessfully");
        this.redirectToAllAUMTargetsPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });
      Sentry.captureException(error);
    }
  }

  redirectToAllAUMTargetsPage(): void {
    this.props.history.push("/aum-targets");
  }

  getAllRoles(): void {
    request({
      query: ALL_ROLES_QUERY,
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          roles: Role[];
        };
      }>): void => {
        this.setState({
          roles: data.data.roles,
        });
      },
    );
  }

  getAUMTaarget(): void {
    const { id } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_AUM_TARGET_SUMMARY,
      variables: { id },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          aumTarget: AUMTarget;
        };
      }>): void => {
        if (data.data.aumTarget) {
          this.setState({
            aumTarget: data.data.aumTarget,
            isLoading: false,
          });
        } else {
          message.error("Failed to Get AUM-Target");
          this.redirectToAllAUMTargetsPage();
        }
      },
    );
  }

  getCurrentPeriod(): void {
    request({
      query: CURRENT_PERIOD_QUERY,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        currentPeriod: data.currentPeriod,
      });
    });
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getAUMTaarget();
      this.getAllRoles();
      this.getCurrentPeriod();
    });
  }

  render(): JSX.Element {
    const { isLoading, roles, aumTarget, currentPeriod } = this.state;

    const fields: FormFieldConfig[] = [
      {
        id: "roleId",
        name: "roleId",
        inputType: "select",
        initialValue: aumTarget?.role.id,
        required: true,
        placeholder: "Role",
        rule: "string",
        label: "Role",
        selectOptions: roles,
        formItemStyle: { marginBottom: "0px" },
        elementStyle: { width: "80%" },
        colConfig: {
          span: 15,
        },
      },

      {
        id: "amount",
        name: "amount",
        inputType: "number",
        initialValue: aumTarget?.amount,
        required: true,
        placeholder: "Amount",
        rule: "number",
        label: "Amount",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 12,
        },
      },
      {
        id: "updateAUMTargetBtn",
        name: "updateAUMTargetBtn",
        inputType: "button",
        value: "Update",
        required: false,
        className: "base-button",
        label: "Button",
        handleClick: this.updateAUMTarget,
        elementStyle: {
          position: "relative",
          top: "-47px",
        },
        colConfig: {
          span: 24,
        },
      },
    ];

    return (
      <Card header="UPDATE AUM-Target">
        <Fragment>
          <div
            style={{
              marginBottom: "12px",
            }}
          >
            <div>
              {currentPeriod.length > 0 && (
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "20px",
                  }}
                >
                  {currentPeriod[0]} - {currentPeriod[11]}
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="update-aum-target-container" style={{}}>
              <Spin spinning={isLoading}>
                <BaseForm
                  gutter={[24, 8]}
                  layout="vertical"
                  reference={this.reference}
                  fieldsToHide={[]}
                  fields={fields}
                />
              </Spin>
            </div>
          </div>
        </Fragment>
      </Card>
    );
  }
}
